import { Component, OnDestroy, OnInit, inject } from '@angular/core';

import { PortfolioProjectService } from '../portfolio-project.service';
import { ProjectCardComponent } from './project-card/project-card.component';
import { Project, Status } from '../interfaces/project';
import { MatChipsModule } from '@angular/material/chips';
import { Subscription, combineLatest, switchMap } from 'rxjs';
import { ViewerSessionService } from '../services/viewer-session/viewer-session.service';
import { UserService } from '../services/user-service/user.service';
import { PROJECT_CATEGORIES } from '../constants/project-categories';

@Component({
  selector: 'app-portfolio-project-list',
  imports: [ProjectCardComponent, MatChipsModule],
  templateUrl: './portfolio-project-list.component.html',
  styleUrl: './portfolio-project-list.component.sass'
})
export class PortfolioProjectListComponent implements OnInit, OnDestroy {
  private readonly projectService = inject(PortfolioProjectService);
  private readonly viewerSession = inject(ViewerSessionService);
  private readonly userService = inject(UserService);

  public projects: Project[] = [];
  public showPrivateBadge = false;

  readonly categoryOptions = [...PROJECT_CATEGORIES];
  readonly statusOptions: string[] = Object.values(Status);

  /** Dynamic tag chips from all projects (excludes status labels and category labels). */
  public tagFilterOptions: string[] = [];
  public selectedCategoryFilters: string[] = [];
  public selectedStatusTagFilters: string[] = [];

  private projectCache: Project[] = [];
  private projectSubscription?: Subscription;

  ngOnInit(): void {
    this.projectSubscription = combineLatest([
      this.viewerSession.canSeeHiddenProjects$,
      this.userService.isAdmin$
    ])
      .pipe(
        switchMap(([canSeeHidden, isAdmin]) => {
          this.showPrivateBadge = canSeeHidden;
          return this.projectService.getProjectsForPortfolioViewer(isAdmin, canSeeHidden);
        })
      )
      .subscribe((projects) => {
        this.projects = projects;
        this.projectCache = projects;
        this.tagFilterOptions = this.collectTagFilterOptions(this.projectCache);
        this.filterProjects();
      });
  }

  ngOnDestroy(): void {
    this.projectSubscription?.unsubscribe();
  }

  private collectTagFilterOptions(projects: Project[]): string[] {
    const statusSet = new Set<string>(Object.values(Status));
    const categorySet = new Set<string>(this.categoryOptions);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const project of projects) {
      project.tags?.forEach((tag) => {
        if (statusSet.has(tag as Status) || categorySet.has(tag)) {
          return;
        }
        if (!seen.has(tag)) {
          seen.add(tag);
          out.push(tag);
        }
      });
    }
    out.sort((a, b) => a.localeCompare(b));
    return out;
  }

  public filterIsSelectedCategory(filter: string): boolean {
    return this.selectedCategoryFilters.includes(filter);
  }

  public filterIsSelectedStatusTag(filter: string): boolean {
    return this.selectedStatusTagFilters.includes(filter);
  }

  public toggleCategoryFilter(filter: string): void {
    this.toggleInArray(filter, this.selectedCategoryFilters);
    this.filterProjects();
  }

  public toggleStatusTagFilter(filter: string): void {
    this.toggleInArray(filter, this.selectedStatusTagFilters);
    this.filterProjects();
  }

  private toggleInArray(value: string, arr: string[]): void {
    const i = arr.indexOf(value);
    if (i >= 0) {
      arr.splice(i, 1);
    } else {
      arr.push(value);
    }
  }

  private filterProjects(): void {
    let result = [...this.projectCache];

    if (this.selectedCategoryFilters.length > 0) {
      result = result.filter((p) =>
        (p.categories ?? []).some((c) => this.selectedCategoryFilters.includes(c))
      );
    }

    if (this.selectedStatusTagFilters.length > 0) {
      const sel = this.selectedStatusTagFilters;
      result = result.filter((p) =>
        sel.some((f) => p.status === f || p.tags?.some((t) => t === f))
      );
    }

    this.projects = result;
  }
}
